import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import BackButton from 'components/BackButton';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';
import { createAPIEndPoint } from 'config/api/api';

function ChangePassword() {
    const textColor = useColorModeValue('navy.700', 'white');

    const [loading, setLoading] = useState(false);
    const userData = getUserData();
    const userId = userData?.id ?? null;

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setLoading(true)
            const data = {
                old_password: values.currentPassword,
                new_password: values.newPassword
            };

            await createAPIEndPoint(`user/change_password/`).update(userId, data);

            toast.success('Password updated successfully');
            setLoading(false)
            resetForm(); // Reset form after successful response
        } catch (error) {
            setLoading(false)
            toast.error(error?.response?.data?.error || 'Failed to change password. Please try again.');
        } finally {
            setSubmitting(false); // Ensure this runs even if API call fails
        }
    };

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const handleShowPassword = (field) => {
        setShowPassword((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string().required('Current password is required'),
            newPassword: Yup.string()
                .min(8, 'New password must be at least 8 characters')
                .required('New password is required'),

        }),
        onSubmit: (values, formikHelpers) => {
            handleSubmit(values, formikHelpers); // ✅ Passing as an object
        },
    });

    return (
        <Box pt={{ base: '80px', md: '80px', xl: '80px' }}>
            <BackButton />
            <Flex align="center" justify="center">
                <Box p={6} borderRadius="lg" boxShadow="lg" bg="white" w={{ base: '90%', md: '500px' }}>
                    <Heading color={textColor} fontSize={{ base: '24px', md: '28px', lg: '32px' }}
                        mb={4}>Change Password</Heading>
                    <form onSubmit={formik.handleSubmit}>
                        {['currentPassword', 'newPassword'].map((field, index) => (
                            <FormControl key={index} isInvalid={formik.touched[field] && formik.errors[field]} mb={4}>
                                <FormLabel color={textColor}>{
                                    field === 'currentPassword' ? 'Current Password' : field === 'newPassword' ? 'New Password' : 'Confirm Password'
                                }</FormLabel>
                                <InputGroup>
                                    <Input
                                        variant="auth"
                                        name={field}
                                        type={showPassword[field] ? 'text' : 'password'}
                                        placeholder={`Enter ${field === 'currentPassword' ? 'current' : field === 'newPassword' ? 'new' : 'confirm'} password`}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values[field]}
                                    />
                                    <InputRightElement>
                                        <Icon
                                            color="gray.400"
                                            as={showPassword[field] ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                            cursor="pointer"
                                            onClick={() => handleShowPassword(field)}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                {formik.touched[field] && formik.errors[field] && (
                                    <Text color="red.500" fontSize="sm">{formik.errors[field]}</Text>
                                )}
                            </FormControl>
                        ))}
                        <Button type="submit" colorScheme="blue" w="full" mt={4} isLoading={loading}>Change Password</Button>
                    </form>
                </Box>
            </Flex>
        </Box>
    );
}

export default ChangePassword;
